/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {BaseFields, BaseModel, BaseProps, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';
import {HistoryAction} from '../util/interfaces';
import {ModelResponse, ResponseData} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import User from "./userModel";
import Invoice from "./invoiceModel";
import {getInvoiceHistory} from "../util/requestSender";
import {IconName} from '@fortawesome/fontawesome-svg-core';

interface Props extends BaseProps {
    action?: HistoryAction;
    date?: string;
    system_action?: boolean;
    user_id?: string;
    invoice_id?: string;
    user?: User | null | any;
    invoice?: Invoice | any;
}

interface Fields extends BaseFields {
    action: HistoryAction;
    date: string;
    system_action: boolean;
    user_id: string;
    invoice_id: string;
    user: User | null;
    invoice: Invoice | null;
}

const fields: Fields = {
    id: '',
    created_at: '',
    updated_at: '',
    action: HistoryAction.CREATE,
    date: '',
    system_action: true,
    user_id: '',
    invoice_id: '',
    user: null,
    invoice: null,
};

const fieldsDefinition: { [key in keyof Omit<Fields, 'invoice' | 'user'>]: FieldDefinition } = {
    id: createFieldDef(ModelFieldType.STRING),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, undefined, true),
    date: createFieldDef(ModelFieldType.STRING),
    system_action: createFieldDef(ModelFieldType.BOOLEAN),
    user_id: createFieldDef(ModelFieldType.STRING),
    invoice_id: createFieldDef(ModelFieldType.STRING),
    action: createFieldDef(ModelFieldType.STRING),
};

const ignoreProps: Array<keyof Props> = [];

export default class History extends BaseModel<Props, Fields> {
    static get = (invoiceId: string, account_id: string, callback: ModelResponse<History[]>) => {
        getInvoiceHistory({
            invoice_id: invoiceId, account_id: account_id, callback: (data: ResponseData) => {
                const history: History[] = data.success ? data.history.map((hist: any) => new History(hist)) : [];
                callback(history, data.success, data);
            }
        });
    };

    static getAll = (invoice: Invoice, callback: ModelResponse<History[]>) => {
        getInvoiceHistory({
            invoice_id: invoice.id, account_id: invoice.accountId, callback: (data: ResponseData) => {
                const history: History[] = data.success ? data.history.map((hist: any) => new History(hist)) : [];
                let hasCreateAction: boolean = false;
                for (const item of history) {
                    if (item.action === HistoryAction.CREATE) {
                        hasCreateAction = true;
                        break;
                    }
                }
                if (history.length && !hasCreateAction)
                    history.push(new History({
                        id: 'created_id',
                        created_at: invoice.createdAt,
                        updated_at: invoice.updatedAt,
                        action: HistoryAction.CREATE,
                        date: invoice.createdAt,
                        system_action: true,
                        invoice,
                    }));
                callback(history, data.success, data);
            }
        });
    };

    constructor(props?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
    }

    get id(): string {
        return this.fields.id as string;
    }

    get action(): HistoryAction {
        return this.fields.action;
    }

    set action(action: HistoryAction) {
        this.fields.action = action;
    }

    get translatedAction(): string {
        switch (this.action) {
            case HistoryAction.CREATE:
                return 'Factuur aangemaakt';
            case HistoryAction.DOWNLOAD:
                return 'Factuur gedownload';
            case HistoryAction.SEND:
                return 'Factuur verzonden';
            case HistoryAction.SEND_REVIEW:
                return 'Review request verzonden';
            case HistoryAction.UPDATE:
                return 'Factuur bijgewerkt';
            case HistoryAction.SEND_OTHER:
                return 'Factuur specifieke mail verzonden';
            case HistoryAction.SEND_PRODUCT:
                return 'Specifieke productmail verzonden';
            case HistoryAction.ORDER_PLACED:
                return 'Bestelling geplaatst';
            case HistoryAction.ORDER_SENT:
                return 'Bestelling verzonden';
        }
    }

    get actionBy(): string {
        if (this.systemAction) return 'Systeem';
        if (this.user) return this.user.fullName;
        return 'Onbekend';
    }

    get date(): string {
        return this.fields.date;
    }

    get dateMoment(): IDate {
        return new IDate(this.date);
    }

    set date(date: string) {
        this.fields.date = date;
    }

    get systemAction(): boolean {
        return this.fields.system_action;
    }

    set systemAction(system_action: boolean) {
        this.fields.system_action = system_action;
    }

    get userId(): string {
        return this.fields.user_id;
    }

    set userId(user_id: string) {
        this.fields.user_id = user_id;
    }

    get invoiceId(): string {
        return this.fields.invoice_id;
    }

    set invoiceId(invoice_id: string) {
        this.fields.invoice_id = invoice_id;
    }

    get user(): User | null {
        return this.fields.user;
    }

    set user(user: User | null) {
        this.fields.user = user;
        if (user) this.userId = user.id;
    }

    get invoice(): Invoice | null {
        return this.fields.invoice;
    }

    set invoice(invoice: Invoice | null) {
        this.fields.invoice = invoice;
        if (invoice) this.invoiceId = invoice.id;
    }

    get actionIcon(): IconName {
        switch (this.action) {
            case HistoryAction.CREATE:
                return 'check-circle';
            case HistoryAction.DOWNLOAD:
                return 'download'
            case HistoryAction.SEND:
                return 'paper-plane';
            case HistoryAction.SEND_REVIEW:
                return 'paper-plane';
            case HistoryAction.UPDATE:
                return 'pen';
            case HistoryAction.SEND_OTHER:
                return 'paper-plane';
            case HistoryAction.SEND_PRODUCT:
                return 'paper-plane';
            case HistoryAction.ORDER_PLACED:
                return 'box';
            case HistoryAction.ORDER_SENT:
                return 'truck';
        }
    }

    save = (callback?: ModelResponse<History>) => {

    };

    update = (callback?: ModelResponse<History>) => {

    };

    delete = (callback?: ModelResponse<History>) => {

    };

    protected build = (props?: Props) => {
        this.constructFields(props);
        if (props) {
            if (props.invoice) {
                const invoice: Invoice = props.invoice instanceof Invoice ? props.invoice : new Invoice(props.invoice);
                invoice.updateHistory(this);
                this.invoice = invoice;
            }
            if (props.user !== undefined) {
                if (props.user) {
                    this.user = props.user instanceof User ? props.user : new User(props.user);
                } else this.user = props.user;
            }
        }
    }
}